import { useEffect, useRef } from "react";
import { buzz } from "../lib/haptics";

// A glowing katana streak that follows a fast finger/pointer swipe. Non-blocking overlay.
export default function SlashTrail() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);

    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };

    const points = [];
    let last = null;
    let lastBuzz = 0;

    const add = (x, y) => {
      const now = performance.now();
      if (last) {
        const dx = x - last.x;
        const dy = y - last.y;
        const dist = Math.hypot(dx, dy);
        const dt = Math.max(1, now - last.t);
        const v = dist / dt; // px per ms
        // only register deliberate fast strokes (a "slash"), not slow scroll drags
        if (v > 1.1) {
          points.push({ x, y, life: 1 });
          if (now - lastBuzz > 90) {
            buzz(8);
            lastBuzz = now;
          }
        }
      }
      last = { x, y, t: now };
    };

    const onPointer = (e) => {
      if (e.pointerType === "mouse" && e.buttons === 0) {
        // trail follows mouse move on desktop too
      }
      add(e.clientX, e.clientY);
    };
    const onTouch = (e) => {
      const t = e.touches[0];
      if (t) add(t.clientX, t.clientY);
    };
    const clearLast = () => { last = null; };

    let raf;
    const render = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = "round";
      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        if (b.life <= 0) continue;
        const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
        grad.addColorStop(0, `rgba(255,255,255,${b.life})`);
        grad.addColorStop(1, `rgba(255,45,45,${b.life * 0.9})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 3.5 * b.life;
        ctx.shadowColor = "rgba(255,60,60,0.9)";
        ctx.shadowBlur = 16 * b.life;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      for (let i = points.length - 1; i >= 0; i--) {
        points[i].life -= 0.055;
        if (points[i].life <= 0) points.splice(i, 1);
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    window.addEventListener("resize", onResize);
    window.addEventListener("pointermove", onPointer, { passive: true });
    window.addEventListener("touchmove", onTouch, { passive: true });
    window.addEventListener("pointerup", clearLast);
    window.addEventListener("touchend", clearLast);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("pointerup", clearLast);
      window.removeEventListener("touchend", clearLast);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="slash-trail"
      className="pointer-events-none fixed inset-0 z-[90]"
      aria-hidden="true"
    />
  );
}
